import type { AuthErrorLike } from './auth-error';

/**
 * What Settings → Devices can honestly show — shared by flashka-web and
 * flashka-app so the two cannot disagree about it.
 *
 * Better Auth's `GET /list-sessions` sits behind `freshSessionMiddleware`: it
 * answers `403 SESSION_NOT_FRESH` once the CURRENT session is older than the
 * auth worker's `session.freshAge` (1 day by default) — measured against the
 * session's `createdAt`, which a refresh never moves. So for anyone who signed
 * in more than a day ago, the list is refused. Both clients rendered that as an
 * EMPTY list, which tells the user they have no sessions at all — a false
 * state, on the one screen whose job is to show where they are signed in.
 *
 * Four answers, kept apart:
 *  - `list`       — the sessions.
 *  - `reauth`     — the server wants a recent sign-in before it lists them.
 *                   Signing out OTHER devices still works (`revoke-other-sessions`
 *                   only needs a live session), so the screen keeps that action.
 *  - `signed-out` — 401: this device's own session is gone (revoked elsewhere).
 *  - `error`      — anything else: offline, a 5xx, a body that is not a list.
 *
 * Pure and free of better-auth types: the client's `{ data, error }` goes in.
 */
export type DevicesListState<T> =
  | { kind: 'list'; sessions: T[] }
  | { kind: 'reauth' }
  | { kind: 'signed-out' }
  | { kind: 'error' };

/** Does this error mean "sign in again first", rather than "it failed"? */
export function isReauthRequired(error: AuthErrorLike): boolean {
  return error?.code === 'SESSION_NOT_FRESH';
}

export function devicesListState<T>(res: {
  data: unknown;
  error: AuthErrorLike;
}): DevicesListState<T> {
  const { data, error } = res;
  if (!error && Array.isArray(data)) return { kind: 'list', sessions: data as T[] };
  // `list-sessions` has no 403 other than freshness, and an older worker may
  // send the status without the code — read either as the same answer.
  if (isReauthRequired(error) || error?.status === 403) return { kind: 'reauth' };
  if (error?.status === 401) return { kind: 'signed-out' };
  return { kind: 'error' };
}
